import { useEffect, useState } from 'react';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { devHubGhecAuthApiRef } from '../../../apis';
import { AlertTitle } from '@material-ui/lab';
import DeleteIcon from '@material-ui/icons/Delete';
import AutoFixHighIcon from '@material-ui/icons/Autorenew';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubCard,
  DevHubChip,
  DevHubCircularProgress,
  DevHubDialog,
  DevHubDialogActions,
  DevHubDialogContent,
  DevHubDialogTitle,
  DevHubDivider,
  DevHubFormControlLabel,
  DevHubGrid,
  DevHubIconButton,
  DevHubSnackbar,
  DevHubSwitch,
  DevHubTextField,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';

interface PromptSuggestion {
  content: string;
  suggestedBy: string;
  suggestedAt: string;
  status: 'pending' | 'approved' | 'denied';
}

interface CustomPrompt {
  content: string;
  active: boolean;
}

// Unified row type for the table
interface TableRow {
  id: string;
  content: string;
  rowType: 'instruction' | 'suggestion';
  // instruction fields
  active?: boolean;
  // suggestion fields
  suggestedBy?: string;
  suggestedAt?: string;
  status?: 'pending' | 'approved' | 'denied';
}

type AddMode = 'instruction' | 'suggestion';

/**
 * AgentPromptSettings
 * @param hasAdminAccess hasAdminAccess flag
 * @constructor
 */
export const AgentPromptSettings = ({
  hasAdminAccess,
}: {
  hasAdminAccess: boolean;
}) => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const githubAuthApi = useApi(devHubGhecAuthApiRef);

  const backendUrl = config.getString('backend.baseUrl');

  const [prompts, setPrompts] = useState<CustomPrompt[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<PromptSuggestion[]>([]);
  const [suggestionSuccess, setSuggestionSuccess] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addMode, setAddMode] = useState<AddMode>('instruction');
  const [content, setContent] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [suggested, setSuggested] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rewriting, setRewriting] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  const getHeaders = async (includeGithub = false) => {
    const [{ token }, githubToken] = await Promise.all([
      identityApi.getCredentials(),
      includeGithub
        ? githubAuthApi
            .getAccessToken(undefined, { optional: true })
            .catch(() => undefined)
        : Promise.resolve(undefined),
    ]);
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(githubToken ? { 'X-GitHub-Token': githubToken } : {}),
    };
  };

  const fetchPrompts = async () => {
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${backendUrl}/api/agent/prompt`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      setPrompts(data.prompts ?? []);
    } catch {
      setFetchError('Failed to load prompts.');
    }
  };

  const fetchSuggestions = async () => {
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${backendUrl}/api/agent/prompt-suggestion`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      setSuggestions(data.suggestions ?? []);
    } catch {
      // non-critical
    }
  };

  useEffect(() => {
    fetchSuggestions();
    if (hasAdminAccess) fetchPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAdminAccess]);

  const openAddDialog = (mode: AddMode) => {
    setAddMode(mode);
    setContent('');
    setErrors([]);
    setWarnings([]);
    setSuggested(null);
    setAddOpen(true);
  };

  const handleContentChange = (e: any) => {
    setContent(e.target.value);
    setErrors([]);
    setWarnings([]);
    setSuggested(null);
  };

  const handleRewrite = async () => {
    if (!content.trim()) return;
    setRewriting(true);
    setSuggested(null);
    setErrors([]);
    setWarnings([]);
    try {
      const headers = await getHeaders(true);
      const res = await fetch(`${backendUrl}/api/agent/prompt/rewrite`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content }),
      });
      const data = await res.json();
      if (data.error) {
        setErrors([
          typeof data.error === 'string' ? data.error : data.error.message,
        ]);
      } else {
        setSuggested(data.suggested ?? null);
      }
    } catch {
      setErrors(['Rewrite failed.']);
    } finally {
      setRewriting(false);
    }
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setLoading(true);
    setErrors([]);
    setWarnings([]);
    setSuggested(null);

    const url =
      addMode === 'instruction'
        ? `${backendUrl}/api/agent/prompt`
        : `${backendUrl}/api/agent/prompt-suggestion`;

    try {
      const headers = await getHeaders();
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content }),
      });
      const data = await res.json();
      if (!data.ok) {
        setErrors(data.errors ?? []);
        setWarnings(data.warnings ?? []);
        setSuggested(data.suggested ?? null);
      } else {
        setWarnings(data.warnings ?? []);
        setSuggested(data.suggested ?? null);
        setContent('');
        setAddOpen(false);
        if (addMode === 'instruction') {
          await fetchPrompts();
        } else {
          setSuggestionSuccess(true);
          await fetchSuggestions();
        }
      }
    } catch {
      setErrors([
        `Failed to ${addMode === 'instruction' ? 'add instruction' : 'submit suggestion'}.`,
      ]);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (prompt: CustomPrompt) => {
    try {
      const headers = await getHeaders();
      await fetch(`${backendUrl}/api/agent/prompt`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          content: prompt.content,
          active: !prompt.active,
        }),
      });
      await fetchPrompts();
    } catch {
      setFetchError('Failed to update prompt.');
    }
  };

  const handleDelete = async (prompt: CustomPrompt) => {
    try {
      const headers = await getHeaders();
      await fetch(`${backendUrl}/api/agent/prompt`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ content: prompt.content }),
      });
      await fetchPrompts();
    } catch {
      setFetchError('Failed to delete prompt.');
    }
  };

  const handleSuggestionAction = async (
    suggContent: string,
    approve: boolean,
  ) => {
    try {
      const headers = await getHeaders();
      await fetch(`${backendUrl}/api/agent/prompt-suggestion`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ content: suggContent, approve }),
      });
      await Promise.all([
        fetchSuggestions(),
        approve ? fetchPrompts() : Promise.resolve(),
      ]);
    } catch {
      // non-critical
    }
  };

  // Build unified table rows: all instructions + only pending/denied suggestions
  const tableRows: TableRow[] = [
    ...prompts.map((p, i) => ({
      id: `instruction-${i}`,
      content: p.content,
      rowType: 'instruction' as const,
      active: p.active,
    })),
    ...suggestions
      .filter(s => s.status !== 'approved')
      .map((s, i) => ({
        id: `suggestion-${i}`,
        content: s.content,
        rowType: 'suggestion' as const,
        suggestedBy: s.suggestedBy,
        suggestedAt: s.suggestedAt,
        status: s.status,
      })),
  ];

  const activeCount = prompts.filter(p => p.active).length;
  const inactiveCount = prompts.filter(p => !p.active).length;
  const pendingCount = suggestions.filter(s => s.status === 'pending').length;

  const submitLabel =
    addMode === 'instruction' ? 'Add Instruction' : 'Submit Suggestion';

  return (
    <DevHubGrid container spacing={3}>
      {hasAdminAccess && (
        <DevHubGrid item xs={12}>
          <DevHubCard
            density="roomy"
            title="Global Instructions"
            subTitle="Active instructions are applied to every user's chat session. Pending suggestions require admin approval."
            actionItem={
              <DevHubBox
                display="flex"
                padding="medium"
                alignItems="center"
                style={{ gap: 8, paddingRight: 16 }}
              >
                <DevHubButton
                  variant="contained"
                  buttonColor="purpleBlue"
                  height="medium"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => openAddDialog('suggestion')}
                >
                  Suggest
                </DevHubButton>
                <DevHubButton
                  variant="contained"
                  buttonColor="blueGreen"
                  height="medium"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => openAddDialog('instruction')}
                >
                  Add New
                </DevHubButton>
              </DevHubBox>
            }
            body={
              <>
                {fetchError && (
                  <DevHubBox mb={2}>
                    <DevHubAlert severity="error">{fetchError}</DevHubAlert>
                  </DevHubBox>
                )}
                <DataTable
                  value={tableRows}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[5, 10, 25]}
                >
                  <Column
                    field="content"
                    header={
                      <DevHubBox
                        display="flex"
                        alignItems="center"
                        style={{ gap: 8 }}
                      >
                        <span>Instruction</span>
                        {activeCount > 0 && (
                          <DevHubChip
                            label={`${activeCount} active`}
                            chipColor="success"
                            chipSize="small"
                            noMargins
                          />
                        )}
                        {inactiveCount > 0 && (
                          <DevHubChip
                            label={`${inactiveCount} inactive`}
                            chipColor="medGray"
                            chipSize="small"
                            noMargins
                          />
                        )}
                        {pendingCount > 0 && (
                          <DevHubChip
                            label={`${pendingCount} pending`}
                            chipColor="warning"
                            chipSize="small"
                            noMargins
                          />
                        )}
                      </DevHubBox>
                    }
                    body={(row: TableRow) => (
                      <DevHubTypography
                        variant="body2"
                        style={{
                          fontFamily: 'monospace',
                          fontSize: 13,
                          opacity:
                            (row.rowType === 'instruction' && !row.active) ||
                            row.status === 'denied'
                              ? 0.45
                              : 1,
                        }}
                      >
                        {row.content}
                      </DevHubTypography>
                    )}
                  />
                  <Column
                    header="Suggested By"
                    style={{ width: '10rem' }}
                    body={(row: TableRow) =>
                      row.rowType === 'suggestion' ? (
                        <DevHubTypography fontSize="medium" variant="caption">
                          {row.suggestedBy}
                        </DevHubTypography>
                      ) : null
                    }
                  />
                  <Column
                    header="Status"
                    headerClassName="p-column-center"
                    style={{ width: '9rem' }}
                    body={(row: TableRow) => (
                      <DevHubBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        {row.rowType === 'suggestion' ? (
                          <DevHubChip
                            label={
                              row.status!.charAt(0).toUpperCase() +
                              row.status!.slice(1)
                            }
                            chipColor={
                              row.status === 'denied' ? 'error' : 'warning'
                            }
                            chipSize="small"
                            noMargins
                          />
                        ) : (
                          <DevHubFormControlLabel
                            style={{ margin: 0 }}
                            control={
                              <DevHubSwitch
                                checked={row.active}
                                onChange={() =>
                                  toggleActive({
                                    content: row.content,
                                    active: row.active!,
                                  })
                                }
                                size="small"
                              />
                            }
                            label=""
                          />
                        )}
                      </DevHubBox>
                    )}
                  />
                  <Column
                    header=""
                    style={{ width: '7rem', textAlign: 'center' }}
                    body={(row: TableRow) => {
                      if (
                        row.rowType === 'suggestion' &&
                        row.status === 'pending'
                      ) {
                        return (
                          <DevHubBox display="flex" style={{ gap: 4 }}>
                            <DevHubTooltip
                              title="Approve & add as instruction"
                              placement="top"
                            >
                              <DevHubIconButton
                                size="small"
                                onClick={() =>
                                  handleSuggestionAction(row.content, true)
                                }
                                style={{ color: '#4caf50' }}
                              >
                                <CheckIcon fontSize="small" />
                              </DevHubIconButton>
                            </DevHubTooltip>
                            <DevHubTooltip
                              title="Deny suggestion"
                              placement="top"
                            >
                              <DevHubIconButton
                                size="small"
                                onClick={() =>
                                  handleSuggestionAction(row.content, false)
                                }
                                style={{ color: '#f44336' }}
                              >
                                <CloseIcon fontSize="small" />
                              </DevHubIconButton>
                            </DevHubTooltip>
                          </DevHubBox>
                        );
                      }
                      if (row.rowType === 'instruction') {
                        return (
                          <DevHubTooltip
                            title="Remove instruction"
                            placement="top"
                          >
                            <DevHubIconButton
                              size="small"
                              onClick={() =>
                                handleDelete({
                                  content: row.content,
                                  active: row.active!,
                                })
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </DevHubIconButton>
                          </DevHubTooltip>
                        );
                      }
                      return null;
                    }}
                  />
                </DataTable>
              </>
            }
          />
        </DevHubGrid>
      )}

      {/* ── Non-admin: suggestions only ── */}
      {!hasAdminAccess && (
        <DevHubGrid item xs={12}>
          <DevHubCard
            density="roomy"
            title="Suggestions"
            subTitle="Suggest an instruction for the agent. Admins will review and approve it."
            actionItem={
              <DevHubBox
                display="flex"
                alignItems="center"
                style={{ gap: 8, paddingRight: 16 }}
              >
                <DevHubButton
                  variant="contained"
                  buttonColor="blueGreen"
                  height="medium"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => openAddDialog('suggestion')}
                >
                  Add New
                </DevHubButton>
              </DevHubBox>
            }
            body={
              <DataTable
                value={suggestions
                  .filter(s => s.status !== 'approved')
                  .map((s, i) => ({ id: i, ...s }))}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25]}
              >
                <Column
                  field="content"
                  header="Instruction"
                  body={(row: PromptSuggestion) => (
                    <DevHubTypography
                      variant="body2"
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 13,
                        opacity: row.status === 'denied' ? 0.45 : 1,
                      }}
                    >
                      {row.content}
                    </DevHubTypography>
                  )}
                />
                <Column
                  field="status"
                  header="Status"
                  headerClassName="p-column-center"
                  style={{ width: '8rem', textAlign: 'center' }}
                  body={(row: PromptSuggestion) => (
                    <DevHubChip
                      label={
                        row.status.charAt(0).toUpperCase() + row.status.slice(1)
                      }
                      chipColor={row.status === 'denied' ? 'error' : 'warning'}
                      chipSize="small"
                      noMargins
                    />
                  )}
                />
                <Column
                  field="suggestedAt"
                  header="Date"
                  style={{ width: '8rem' }}
                  body={(row: PromptSuggestion) =>
                    new Date(row.suggestedAt).toLocaleDateString()
                  }
                />
              </DataTable>
            }
          />
        </DevHubGrid>
      )}

      {/* ── Add dialog ── */}
      <DevHubDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DevHubDialogTitle>
          {addMode === 'instruction'
            ? 'Add New Instruction'
            : 'Add New Suggestion'}
        </DevHubDialogTitle>
        <DevHubDialogContent>
          <DevHubBox mb={1}>
            <DevHubTypography variant="caption" style={{ opacity: 0.6 }}>
              {addMode === 'instruction'
                ? 'Instructions are applied immediately to all users. Only visible to agent admins.'
                : 'Suggestions require approval from an agent admin before taking effect.'}
            </DevHubTypography>
          </DevHubBox>
          <DevHubDivider style={{ margin: '8px 0 16px 0' }} />
          <DevHubBox style={{ paddingRight: 50 }}>
            <DevHubTextField
              label={addMode === 'instruction' ? 'Instruction' : 'Suggestion'}
              multiline
              minRows={3}
              fullWidth
              value={content}
              onChange={handleContentChange}
              placeholder="e.g. MUST always search both word orderings when looking up roles."
            />
          </DevHubBox>
          {errors.length > 0 && (
            <DevHubBox mt={2}>
              <DevHubAlert severity="error">
                <AlertTitle>Cannot save</AlertTitle>
                {errors.map((e, i) => (
                  <div key={i}>{e}</div>
                ))}
              </DevHubAlert>
            </DevHubBox>
          )}
          {warnings.length > 0 && (
            <DevHubBox mt={2}>
              <DevHubAlert severity="warning">
                <AlertTitle>Prompt quality warning</AlertTitle>
                {warnings.map((w, i) => (
                  <div key={i}>{w}</div>
                ))}
              </DevHubAlert>
            </DevHubBox>
          )}
          {suggested && (
            <DevHubBox mt={2}>
              <DevHubAlert severity="info">
                <AlertTitle>Suggested rewrite</AlertTitle>
                <DevHubBox
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 13,
                    margin: '8px 0',
                  }}
                >
                  {suggested}
                </DevHubBox>
                <DevHubButton
                  variant="outlined"
                  color="primary"
                  height="small"
                  onClick={() => {
                    setContent(suggested);
                    setSuggested(null);
                    setWarnings([]);
                    setErrors([]);
                  }}
                >
                  Use this
                </DevHubButton>
              </DevHubAlert>
            </DevHubBox>
          )}
        </DevHubDialogContent>
        <DevHubDialogActions>
          <DevHubButton
            variant="outlined"
            color="secondary"
            height="large"
            onClick={() => setAddOpen(false)}
          >
            Cancel
          </DevHubButton>
          <DevHubButton
            variant="outlined"
            color="primary"
            height="large"
            onClick={handleRewrite}
            disabled={rewriting || !content.trim()}
            startIcon={
              rewriting ? (
                <DevHubCircularProgress size={14} />
              ) : (
                <AutoFixHighIcon fontSize="small" />
              )
            }
          >
            {rewriting ? 'Rewriting…' : 'Rewrite with AI'}
          </DevHubButton>
          <DevHubButton
            variant="contained"
            color="primary"
            height="large"
            onClick={handleSubmit}
            disabled={loading || !content.trim()}
            startIcon={
              loading ? (
                <DevHubCircularProgress size={14} />
              ) : (
                <AddIcon fontSize="small" />
              )
            }
          >
            {loading ? 'Saving…' : submitLabel}
          </DevHubButton>
        </DevHubDialogActions>
      </DevHubDialog>

      {/* ── Success snackbar ── */}
      <DevHubSnackbar
        open={suggestionSuccess}
        autoHideDuration={4000}
        onClose={() => setSuggestionSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <DevHubAlert
          severity="success"
          onClose={() => setSuggestionSuccess(false)}
        >
          Suggestion submitted for admin review.
        </DevHubAlert>
      </DevHubSnackbar>
    </DevHubGrid>
  );
};
